/**
 * Книга нормативов ЕТС деревом (AK-723) — типы и запросы.
 *
 * Структуру продиктовал founder 26.08:
 *
 *     Культивация
 *     |- глубина 6-8 см
 *     |-- МТЗ-2022
 *     |--- КСК-12
 *     |---- 12 м
 *
 * Транспортные — то же дерево короче на два уровня: у перегона нет ни орудия,
 * ни ширины захвата. Лестницу присылает сервер (`ladder`), клиент её НЕ
 * дублирует — иначе два места знают про «у транспорта уровней меньше», и они
 * разъедутся.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { budgetErrorText } from '@/lib/planning/budgetCatalog';

export type NormsLevel = 'work' | 'params' | 'prime_mover' | 'implement' | 'width' | 'leaf';

/**
 * Лист конкретной книги. Книга = КЛАСТЕР (ADR-073 D14): РК → Ростов,
 * СК → Ставрополь, ВК → Воронеж. Экран показывает ОДНУ книгу за раз — иначе
 * узел «Пахота» сложил бы расценки трёх регионов в один диапазон, число,
 * которого нет ни в одной книге.
 */
export interface BookTab {
  workbook: string;
  sheet: string;
  work_kind: 'field' | 'transport';
  rows_count: number;
  /** null — привязки к кластеру нет; книга подписана именем файла (НК/КК ещё не залиты). */
  cluster_id: string | null;
  cluster_name: string | null;
  /** Код региона из папок финансистов: РК / СК / ВК. */
  cluster_code: string | null;
  /** Готовая подпись для селектора: «РК · Ростов» либо имя файла. */
  book_label: string;
}

export interface NormsTreeNode {
  key: string;
  label: string;
  rows_count: number;
  pay_min: number | null;
  pay_max: number | null;
  fuel_min: number | null;
  fuel_max: number | null;
  unit_symbol: string | null;
  fuel_unit_symbol: string | null;
  /** Написание единицы из книги — запасной вариант, когда единица не сматчена. */
  unit_raw: string | null;
  fuel_unit_raw: string | null;
  /** Позиции бюджета за узлом — только на уровне «работа × параметры». */
  positions: {
    id: string;
    name: string;
    planned_price: number | null;
    /** Единица оплаты: «Подвоз семян» ₽/га и ₽/т — разные деньги, не дубль. */
    unit_symbol: string | null;
  }[];
}

export interface NormsBookRow {
  id: string;
  prime_mover: string | null;
  implement: string | null;
  implement_count: number | null;
  width_m: number | null;
  norm_per_shift: number | null;
  pay_per_unit: number | null;
  unit_symbol: string | null;
  /** Написание единицы из книги. Запасной вариант, когда единица не сматчена
   *  на справочник: у транспортных строк `pay_unit_id` пуст (дефект загрузчика
   *  — алиас «час» не сматчен), и без `unit_raw` колонка была бы пустой. */
  unit_raw: string | null;
  fuel_rate: number | null;
  fuel_unit_symbol: string | null;
  fuel_unit_raw: string | null;
  machines_ambiguous: boolean;
  executor_hint_raw: string | null;
  source_workbook: string | null;
  source_sheet: string | null;
  source_row: number | null;
  /** Строку правил человек. Этим же следом правка защищена от перезаливки книги. */
  is_overridden: boolean;
  /** Какие ИМЕННО поля правлены — подсвечиваются только они (ADR-073 D12). */
  overridden_fields: string[];
  updated_by: string | null;
  updated_at: string | null;
}

export interface NormsTreeOut {
  level: NormsLevel;
  ladder: NormsLevel[];
  nodes: NormsTreeNode[];
  rows: NormsBookRow[];
}

export interface NormsSearchHit {
  work_id: string;
  work_label: string;
  params: string;
  prime_mover: string;
  implement: string;
  width: string;
  work_kind: 'field' | 'transport';
  rows_count: number;
}

/**
 * Правимые поля строки книги — ЗЕРКАЛО белого списка бэка
 * (`services/labor_norm_edit.py::EDITABLE_FIELDS`, ADR-073 D11).
 *
 * Подписи те же, что уходят в лог правок: человек, читающий историю справочника,
 * и человек, правящий строку, обязаны видеть одно слово, а не «оплата за
 * единицу» в логе против «расценка» на экране.
 *
 * Всё, чего здесь нет (`source_workbook` / `source_sheet` / `source_row` /
 * `work_name_raw`), — АДРЕС строки в книге: бэк отвечает на него 422
 * (`extra='forbid'`), и экран обязан такие поля даже не показывать полем ввода.
 */
export const NORM_FIELD_LABELS: Record<string, string> = {
  norm_per_shift: 'норма выработки за смену',
  norm_per_shift_t: 'норма выработки за смену, т',
  fuel_rate: 'расход топлива',
  hazard_pct: 'вредность, %',
  variable_pct: 'переменная часть, %',
  sprout_bonus_pct: 'за всходы, %',
  total_pay_per_unit: 'оплата за единицу',
  total_pay_per_norm: 'оплата за норму',
  pay_unit_id: 'единица оплаты',
  fuel_unit_id: 'единица ГСМ',
  company_op_type_id: 'тип операции',
};

/** Строка книги ЦЕЛИКОМ — то, что открывает диалог «Изменить строку». */
export interface NormDetail {
  id: string;
  work_kind: 'field' | 'transport';
  company_op_type_id: string | null;
  op_type_name: string | null;
  work_name_raw: string;
  params_raw: string | null;
  prime_mover_key: string | null;
  implement_key: string | null;
  width_m: number | null;
  norm_per_shift: number | null;
  norm_per_shift_t: number | null;
  fuel_rate: number | null;
  hazard_pct: number | null;
  variable_pct: number | null;
  sprout_bonus_pct: number | null;
  total_pay_per_unit: number | null;
  total_pay_per_norm: number | null;
  pay_unit_id: string | null;
  fuel_unit_id: string | null;
  pay_uom_raw: string | null;
  fuel_uom_raw: string | null;
  source_workbook: string;
  source_sheet: string;
  source_row: number;
  archived_at: string | null;
  is_overridden: boolean;
  overridden_fields: string[];
}

/** Тело PATCH. Присылается ТОЛЬКО то, что человек менял (см. `usePatchNorm`). */
export type NormPatch = Partial<{
  norm_per_shift: number | null;
  norm_per_shift_t: number | null;
  fuel_rate: number | null;
  hazard_pct: number | null;
  variable_pct: number | null;
  sprout_bonus_pct: number | null;
  total_pay_per_unit: number | null;
  total_pay_per_norm: number | null;
  pay_unit_id: string | null;
  fuel_unit_id: string | null;
  company_op_type_id: string | null;
}>;

export function useNormDetail(normId: string | null) {
  return useQuery<NormDetail, Error>({
    queryKey: ['planning', 'labor-norms', 'detail', normId],
    queryFn: () => api.get<NormDetail>(`/api/v1/planning/labor-norms/${normId}`),
    enabled: !!normId,
    staleTime: 60_000,
  });
}

/**
 * Инвалидация после правки строки книги.
 *
 * Дерево ЦЕЛИКОМ, а не одна строка: расценка живёт в диапазоне узла
 * («45,41 – 225,42 ₽/га»), и правка одной строки двигает границы диапазона на
 * всех уровнях над ней. Обновить лист и оставить старый диапазон в родителе —
 * это показать два разных ответа на один вопрос на одном экране.
 *
 * `season-catalogs` — потому что каждая правка на бэке зовёт `note_catalog_edit`
 * и переводит справочник сезона в «Пересмотрен»: бейдж статуса стоит на этом же
 * экране и обязан ожить сразу, а не по истечении staleTime.
 */
function useInvalidateNorms() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['planning', 'labor-norms'] });
    void qc.invalidateQueries({ queryKey: ['planning', 'season-catalogs'] });
    // Позиции бюджета висят на узле «работа × параметры» этого же дерева.
    void qc.invalidateQueries({ queryKey: ['planning', 'budget-catalog'] });
  };
}

/**
 * Отказ бэка показываем ДОСЛОВНО (422 «Сезон не найден в этой компании»,
 * «Единица измерения ГСМ не найдена в справочнике»): бэк уже написал причину
 * по-человечески, и подменять её общим «не удалось сохранить» значит стереть
 * единственную подсказку, что делать дальше.
 */
function notifyFailure(err: unknown): void {
  toast.error(budgetErrorText(err));
}

/**
 * Правка строки книги. `season_id` обязателен — правка справочника это событие
 * КОНКРЕТНОГО сезона (лог и статус-машина живут в сезоне), и бэк его не
 * угадывает. Нет сезона на экране — правка недоступна, а не «сохраним куда-нибудь».
 */
export function usePatchNorm(seasonId: string) {
  const invalidate = useInvalidateNorms();
  return useMutation<NormDetail, Error, { id: string; patch: NormPatch }>({
    mutationFn: ({ id, patch }) =>
      api.patch<NormDetail>(
        `/api/v1/planning/labor-norms/${id}?season_id=${encodeURIComponent(seasonId)}`,
        patch,
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/** «Убрать строку» — АРХИВ, не удаление: строка книги документ, на неё ссылаются. */
export function useArchiveNorm(seasonId: string) {
  const invalidate = useInvalidateNorms();
  return useMutation<NormDetail, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api.post<NormDetail>(
        `/api/v1/planning/labor-norms/${id}/archive?season_id=${encodeURIComponent(seasonId)}`,
        {},
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/** Путь в дереве. Длина = глубина; `undefined` дальше = «этот уровень и спрашиваем». */
export interface NormsPath {
  work_id?: string;
  params?: string;
  prime_mover?: string;
  implement?: string;
  width?: string;
}

function pathParams(path: NormsPath): Record<string, string> {
  const out: Record<string, string> = {};
  // Пустая строка — ЗНАЧИМОЕ значение («без уточнения», «ширина не указана»),
  // поэтому проверяем на undefined, а не на truthiness. `if (path.params)`
  // потерял бы весь узел «без уточнения» — а под ним 21 работа.
  if (path.work_id !== undefined) out.work_id = path.work_id;
  if (path.params !== undefined) out.params = path.params;
  if (path.prime_mover !== undefined) out.prime_mover = path.prime_mover;
  if (path.implement !== undefined) out.implement = path.implement;
  if (path.width !== undefined) out.width = path.width;
  return out;
}

export function useNormsBooks() {
  return useQuery<BookTab[], Error>({
    queryKey: ['planning', 'labor-norms', 'books'],
    queryFn: () => api.get<BookTab[]>('/api/v1/planning/labor-norms/books'),
    staleTime: 10 * 60_000,
  });
}

/**
 * Один уровень дерева. Ленивый по построению: хук живёт в компоненте уровня,
 * а компонент монтируется только когда родительский узел раскрыт. Грузить всё
 * дерево разом — это тысячи строк книги ради двух-трёх раскрытых веток.
 */
export function useNormsTree(args: {
  workKind: 'field' | 'transport';
  workbook?: string;
  sheet?: string;
  seasonId?: string | null;
  path: NormsPath;
  q?: string;
  enabled?: boolean;
}) {
  const { workKind, workbook, sheet, seasonId, path, q, enabled = true } = args;
  const search = {
    work_kind: workKind,
    ...(workbook ? { workbook } : {}),
    ...(sheet ? { sheet } : {}),
    ...(seasonId ? { season_id: seasonId } : {}),
    ...pathParams(path),
    ...(q && q.length >= 2 ? { q } : {}),
  };
  return useQuery<NormsTreeOut, Error>({
    queryKey: ['planning', 'labor-norms', 'tree', search],
    queryFn: () =>
      api.get<NormsTreeOut>(
        `/api/v1/planning/labor-norms/tree?${new URLSearchParams(search).toString()}`,
      ),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Пути до совпадений — чтобы раскрыть ветки, а не просто отфильтровать список. */
export function useNormsSearch(args: {
  q: string;
  workbook?: string;
  sheet?: string;
  enabled?: boolean;
}) {
  const { q, workbook, sheet, enabled = true } = args;
  const search = {
    q,
    ...(workbook ? { workbook } : {}),
    ...(sheet ? { sheet } : {}),
  };
  return useQuery<NormsSearchHit[], Error>({
    queryKey: ['planning', 'labor-norms', 'search', search],
    queryFn: () =>
      api.get<NormsSearchHit[]>(
        `/api/v1/planning/labor-norms/search?${new URLSearchParams(search).toString()}`,
      ),
    enabled: enabled && q.length >= 2,
    staleTime: 60_000,
  });
}

/** Ключ пути до узла — им и только им опознаётся, что раскрывать. */
export function pathKey(path: NormsPath, key: string): string {
  return [path.work_id, path.params, path.prime_mover, path.implement, path.width]
    .filter((part) => part !== undefined)
    .concat(key)
    .join('\u0000');
}

/**
 * ПУТИ, которые надо раскрыть, чтобы совпадения стали видны.
 *
 * Именно пути, а не имена узлов. Имена в книге повторяются: «глубина 6-8 см»
 * есть у Культивации, Дискования и Боронования, «КСК-12» — у половины работ.
 * Множество имён на глубине раскрывало бы одноимённые узлы во ВСЕХ ветках
 * сразу, и вместо трёх найденных строк человек получал бы развёрнутое пол-дерева.
 *
 * Возвращаются все префиксы пути: чтобы совпадение стало видно, раскрыть надо
 * всю дорогу сверху, а не только конечный узел.
 */
export function expandPathsFromHits(hits: NormsSearchHit[]): Set<string> {
  const paths = new Set<string>();
  for (const hit of hits) {
    const chain = [hit.work_id, hit.params, hit.prime_mover, hit.implement, hit.width];
    for (let depth = 0; depth < chain.length; depth += 1) {
      if (chain[depth] === undefined || chain[depth] === null) break;
      paths.add(chain.slice(0, depth + 1).join('\u0000'));
    }
  }
  return paths;
}
